import { PlayerGameMinutesWithRels } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { atomWithDebounce } from '@/atoms/debounce';
import { playerIdAtom } from '@/atoms/player/player';
import { ALL_ITEMS } from '@/consts/interface';
import { Nullable } from '@/types/generic';
import { NestedObject } from '@/types/object';
import { convertFilterParamsToString, hasEmptyInFilters } from '@/utils/api';
import { fetchClientAtom } from '../client';
import { rawPlayerRadarDataAtom } from './playerRadarData';

export const playerMinutesPlayedFilterDebounceObject = atomWithDebounce<Nullable<NestedObject>>(null);
const { debouncedValueAtom: playerMinutesPlayedFilterAtom } = playerMinutesPlayedFilterDebounceObject;

export const playerMinutesPlayedAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const filterParams = get(playerMinutesPlayedFilterAtom);
  const queryKey = ['playerMinutesPlayed', get(playerIdAtom), filterParams] as const;
  const queryFn = async ({ queryKey: [, playerId, params] }: { queryKey: typeof queryKey }) => {
    if (hasEmptyInFilters(params)) return [] satisfies PlayerGameMinutesWithRels[];
    return (await fetch(
      `/player/${playerId}/games/minutes-played${convertFilterParamsToString({
        ...params,
        limit: ALL_ITEMS,
        order_by: { 'game.date': 'ASC' },
      })}`,
    )) as PlayerGameMinutesWithRels[];
  };

  return { queryKey, queryFn };
});

export const playerAggsMinutesPlayedAtom = atom(async (get): Promise<number | undefined> => {
  const rawPlayerRadarData = (await get(rawPlayerRadarDataAtom)).data;
  return rawPlayerRadarData.minutes_played;
});

export const playerAggsMinutesPlayedWithFallbackAtom = atom(async get => {
  const minutesPlayed = await get(playerAggsMinutesPlayedAtom);
  return minutesPlayed || 0;
});

export const unwrappedPlayerAggsMinutesPlayedAtom = unwrap(playerAggsMinutesPlayedWithFallbackAtom, prev => prev || 0);

const playerAggsMinutesPlayed360Atom = atom(async get => {
  const rawPlayerRadarData = (await get(rawPlayerRadarDataAtom)).data;
  return rawPlayerRadarData.minutes_played_360 || 0;
});

export const unwrappedPlayerAggsMinutesPlayed360Atom = unwrap(playerAggsMinutesPlayed360Atom, prev => prev || 0);
